import he from 'he';

/**
 * Text that arrives HTML-escaped from an upstream source.
 *
 * Publication metadata comes from Crossref/PubMed and protein annotations from
 * NCBI/EBI, and both hand over text that was written for an HTML page:
 * "Respiratory Physiology &amp; Neurobiology", "Stargardt&#39;s disease",
 * "2&#39;-deoxyribonucleotide metabolic process". React renders text nodes
 * literally -- that is the whole point of it -- so an entity that reaches a
 * grid cell is shown as its source text instead of the character it stands for.
 *
 * Decoded here, where the data enters the client, rather than at each render:
 * a value that is decoded on its way in is the same string everywhere
 * afterwards, so the grid's sorting, its quick filter and the export all agree
 * with what is on screen. Decoding in a cell renderer would leave the other
 * three reading the raw text.
 *
 * Not a substitute for escaping on output. Nothing here is inserted as HTML --
 * these strings go into text nodes -- so decoding cannot introduce markup.
 */

/** Decode one string. Anything that is not a string is passed through. */
export function decodeText(value) {
  if (typeof value !== 'string') return value;
  // `he.decode` also resolves legacy entities written without the trailing
  // semicolon, which is what makes it wrong for URLs: "?a=1&copy=2" would come
  // back with a ©. Only ever call this on prose, never on a link, DOI or id --
  // hence named fields below rather than a walk over every string.
  if (!value.includes('&')) return value;
  return he.decode(value);
}

/** A shallow copy of `obj` with the named string fields decoded. */
export function decodeFields(obj, keys) {
  if (!obj || typeof obj !== 'object') return obj;
  let out = obj;
  for (const key of keys) {
    const decoded = decodeText(obj[key]);
    if (decoded !== obj[key]) {
      if (out === obj) out = { ...obj };
      out[key] = decoded;
    }
  }
  return out;
}

/** Free-text fields of a study sheet. Deliberately excludes `url`. */
const STUDY_SHEET_TEXT = [
  'journal', 'study_origin', 'detection_method', 'pex_substrate',
  'pex_amount', 'extraction', 'analytical_platform', 'detection_criteria',
  'protein_id_selection',
];

/**
 * A published study as the API returns it, with its prose decoded.
 *
 * `abstract` is deliberately absent. It carries JATS/HTML markup, and its
 * entities belong to that markup's escaping: `cleanAbstract` in Study.jsx
 * strips the tags first and decodes second, which is the only order that
 * works. Decode "&lt;" up here and "p &lt; 0.05 were
 * <jats:italic>significant</jats:italic>" loses "0.05 were" to the
 * tag-stripping regex.
 */
export function decodeStudy(study) {
  if (!study || typeof study !== 'object') return study;
  const out = decodeFields(study, ['title', 'authors']);
  const sheet = decodeFields(study.study_sheet, STUDY_SHEET_TEXT);
  if (sheet === study.study_sheet) return out;
  return { ...out, study_sheet: sheet };
}

/** Term lists inside `Protein.info` whose `name` is human-readable prose. */
const INFO_TERM_LISTS = [
  'disgenet', 'goterm_bp_all', 'goterm_bp_all_filtered',
  'goterm_cc_all', 'goterm_cc_all_filtered',
  'goterms_bp_all', 'goterms_cc_all', 'goterm_mf_all',
];

/** `Protein.info` with the `name` of every annotation term decoded. */
export function decodeProteinInfo(info) {
  if (!info || typeof info !== 'object') return info;
  let out = info;
  for (const key of INFO_TERM_LISTS) {
    const list = info[key];
    if (!Array.isArray(list)) continue;
    let changed = false;
    const mapped = list.map(term => {
      const decoded = decodeFields(term, ['name']);
      if (decoded !== term) changed = true;
      return decoded;
    });
    if (changed) {
      if (out === info) out = { ...info };
      out[key] = mapped;
    }
  }
  return out;
}
